import { faXmark } from "@fortawesome/pro-regular-svg-icons/faXmark";
import Box from "@mui/material/Box";
import { buttonClasses } from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import { keyframes, styled, type Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { closeSnackbar, CustomContentProps } from "notistack";
import { forwardRef } from "react";
import { useTranslation } from "../../../i18n";
import { FaIcon } from "../../DataDisplay/Icon/FaIcon";
import { Button } from "../../Inputs/Button/Button";
import { useProgressState } from "./progress-store";
import type { ToastSeverity } from "./toast-duration";
import { toastSeverityIcons } from "./toast-icons";
import type { ProgressContentExtraProps, ToastContentExtraProps } from "./toast-options";
import { useDismissTimer } from "./use-dismiss-timer";

type Surface = ToastSeverity | "progress";

// The stack's column width above `md`. Fixed rather than content-sized so a stack
// reads as one column instead of a ragged edge. Notistack's 288 px minimum was too
// narrow once the close button and gaps took their share: 182 px of text, so a
// one-sentence failure wrapped to three lines. 360 px gives about 40 characters a
// line and sits inside the Material 344–672 px range. An action shares the row with
// the text rather than widening the column; toast copy is ours to keep short.
export const TOAST_WIDTH = 360;

// One visual family for every toast: outcome kinds paint the filled Alert tokens for
// their severity, so a toast and a filled Alert are one surface in both colour
// schemes; progress has no severity, so it keeps a neutral dark card. Full width
// below `md`, one column above it. The flex row serves progress toasts; outcome
// toasts overlay a grid (`outcomeLayoutSx`).
const ToastRoot = styled("div", {
  shouldForwardProp: (prop) => prop !== "surface",
})<{ surface: Surface }>(({ theme, surface }) => ({
  position: "relative",
  overflow: "hidden",
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(1.5),
  padding: theme.spacing(1, 2),
  width: "100%",
  borderRadius: 4,
  boxShadow: theme.shadows[6],
  ...(surface === "progress"
    ? {
        backgroundColor: theme.vars.palette.grey[800],
        color: theme.vars.palette.grey[50],
      }
    : {
        backgroundColor: theme.vars.palette.Alert[`${surface}FilledBg`],
        color: theme.vars.palette.Alert[`${surface}FilledColor`],
      }),
  [theme.breakpoints.up("md")]: {
    width: TOAST_WIDTH,
  },
}));

// Outcome toasts: icon, text, action and close in one row. Text takes what the action
// leaves and wraps; named areas let an absent action leave its column empty.
const outcomeLayoutSx = (theme: Theme) => ({
  display: "grid",
  columnGap: theme.spacing(1.5),
  alignItems: "start",
  gridTemplateColumns: "auto minmax(0, 1fr) auto auto",
  gridTemplateAreas: `"icon text action close"`,
});

// Any button the caller passes is redrawn outlined in the toast's text colour, so a
// call to action reads as a button rather than a bare label. Hover and press tint with
// currentColor, which also holds where the text is dark on a light surface.
const actionSlotSx = {
  gridArea: "action",
  alignSelf: "center",
  display: "flex",
  [`& .${buttonClasses.root}`]: {
    color: "inherit",
    border: "1px solid currentColor",
    backgroundColor: "transparent",
    padding: "3px 10px",
    whiteSpace: "nowrap",
    "&:hover": { backgroundColor: "color-mix(in srgb, currentColor 12%, transparent)" },
    "&:active": { backgroundColor: "color-mix(in srgb, currentColor 20%, transparent)" },
  },
} as const;

// Leading icons sit on the first text line: the body has 4 px of vertical padding
// and body2 is a 19 px line, so a 20 px icon with the same 4 px offset is centred
// on it to within half a pixel.
const leadingIconSx = { mt: 0.5, flexShrink: 0 } as const;

const shrink = keyframes({
  from: { transform: "scaleX(1)" },
  to: { transform: "scaleX(0)" },
});

// Deliberately kept under prefers-reduced-motion: the bar is the only signal of
// how long a toast has left, and it is already linear. A shrinking edge bar always
// means time left; the growing bar in a progress toast's body always means work done.
const CountdownBar = styled("div")({
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
  height: 3,
  backgroundColor: "currentColor",
  opacity: 0.35,
  transformOrigin: "left",
  animationName: shrink,
  animationTimingFunction: "linear",
  animationFillMode: "forwards",
});

type ToastContentProps = CustomContentProps & ToastContentExtraProps;

/**
 * Renders every success, info, warning and error toast. Lifecycle comes from the
 * façade (`duration`), not from anything the caller can pass: the kit draws the
 * close button, the Undo button and the countdown, and owns the dismiss clock.
 */
export const ToastContent = forwardRef<HTMLDivElement, ToastContentProps>(function ToastContent(
  { id, message, action, severity, category, duration, title, undo, testId, style, className },
  ref,
) {
  const { t } = useTranslation("toast");
  const { paused, bindings } = useDismissTimer(duration, () => closeSnackbar(id));
  const Icon = toastSeverityIcons[severity];
  const isFailure = severity === "warning" || severity === "error";
  const hasTitle = title != null && title !== "";
  const callerAction = typeof action === "function" ? action(id) : action;
  // The action, when there is one, shares the row with the message (`actionSlotSx`).
  const actionNode = undo ? (
    <Button
      variant="text"
      color="inherit"
      size="small"
      onClick={() => {
        undo();
        closeSnackbar(id);
      }}
    >
      {t("undo")}
    </Button>
  ) : (
    callerAction
  );

  return (
    <ToastRoot
      ref={ref}
      surface={severity}
      // Failures interrupt (an assertive alert); routine outcomes wait their turn (a polite status).
      role={isFailure ? "alert" : "status"}
      className={className}
      style={style}
      data-severity={severity}
      data-category={category}
      data-duration={duration ?? "sticky"}
      data-testid={testId}
      sx={outcomeLayoutSx}
      {...bindings}
    >
      <Icon sx={{ ...leadingIconSx, gridArea: "icon", fontSize: 20 }} />
      <Box sx={{ gridArea: "text", minWidth: 0, py: 0.5, overflowWrap: "anywhere" }}>
        {hasTitle ? (
          <Typography variant="subtitle2" component="div">
            {title}
          </Typography>
        ) : null}
        <Typography variant="body2" component="div" sx={{ whiteSpace: "pre-line" }}>
          {message}
        </Typography>
      </Box>
      {actionNode ? <Box sx={actionSlotSx}>{actionNode}</Box> : null}
      <IconButton
        size="small"
        color="inherit"
        aria-label={t("dismiss")}
        onClick={() => closeSnackbar(id)}
        // The theme's IconButton root override pins every colour, `inherit` included, to
        // `on.surfaceDimmer`, which reads as dark grey on these coloured surfaces.
        sx={{ gridArea: "close", alignSelf: "center", color: "inherit" }}
      >
        <FaIcon icon={faXmark} fontSize="small" />
      </IconButton>
      {duration !== null ? (
        <CountdownBar
          aria-hidden
          data-countdown=""
          data-testid="toast-countdown"
          style={{
            animationDuration: `${duration}ms`,
            animationPlayState: paused ? "paused" : "running",
          }}
        />
      ) : null}
    </ToastRoot>
  );
});

const clampPercent = (value: number) => Math.min(100, Math.max(0, value));

type ProgressToastContentProps = CustomContentProps & ProgressContentExtraProps;

/**
 * Renders a progress toast. It has no close button and no countdown: it ends
 * only through its `ProgressHandle`, and its message and value come from the
 * progress store so updates never re-enqueue.
 */
export const ProgressToastContent = forwardRef<HTMLDivElement, ProgressToastContentProps>(
  function ProgressToastContent({ id, message, testId, style, className }, ref) {
    const live = useProgressState(id);
    const value = live?.value;
    const determinate = typeof value === "number";

    return (
      <ToastRoot
        ref={ref}
        surface="progress"
        role="status"
        aria-busy="true"
        className={className}
        style={style}
        data-severity="progress"
        data-duration="progress"
        data-testid={testId}
      >
        <CircularProgress aria-hidden size={20} color="inherit" sx={leadingIconSx} />
        <Box sx={{ flex: 1, minWidth: 0, py: 0.5 }}>
          <Typography variant="body2" component="div">
            {live?.message ?? message}
          </Typography>
          <LinearProgress
            variant={determinate ? "determinate" : "indeterminate"}
            value={determinate ? clampPercent(value) : undefined}
            color="inherit"
            sx={{ mt: 1 }}
          />
        </Box>
      </ToastRoot>
    );
  },
);
